import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { useLanguage } from '../../context/LanguageContext';
import { Card, CardContent } from '../../components/ui/card';
import { Camera, Calendar, Image as ImageIcon } from 'lucide-react';
import { AutoTranslate } from '../../hooks/useAutoTranslate';
import usePageBanner from '../../hooks/usePageBanner';
import { getAlbums } from '../../services/api';

const getLocalized = (obj, lang) => {
  if (!obj) return '';
  if (typeof obj === 'string') return obj;
  return obj[lang] || obj.sv || obj.en || Object.values(obj)[0] || '';
};

const formatEventDate = (iso, lang) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(lang === 'sv' ? 'sv-SE' : lang, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return '';
  }
};

const AlbumsListPage = () => {
  const { language, isRTL } = useLanguage();
  const { bannerImage, bannerTitle, bannerSubtitle } = usePageBanner('albums', language);
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    (async () => {
      try {
        const data = await getAlbums();
        setAlbums(data || []);
      } catch (err) {
        console.error('Error loading albums:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen themed-page" dir={isRTL ? 'rtl' : 'ltr'} data-testid="albums-list-page">
      <Navbar />

      {/* Hero banner */}
      <section className="themed-hero relative h-80 overflow-hidden">
        <img
          src={bannerImage || 'https://images.unsplash.com/photo-1519741497674-611481863552?w=1920'}
          alt="Album"
          className="w-full h-full object-cover"
        />
        <div className={`absolute inset-0 bg-gradient-to-r ${isRTL ? 'from-black/40 to-black/70' : 'from-black/70 to-black/40'} flex items-center`}>
          <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full ${isRTL ? 'text-right' : ''}`}>
            <h1 className="responsive-hero-title font-serif font-bold text-white mb-4 fade-in">
              {bannerTitle || <AutoTranslate>Fotoalbum</AutoTranslate>}
            </h1>
            <p className="responsive-hero-subtitle text-lg sm:text-xl text-gray-200 fade-in">
              {bannerSubtitle || <AutoTranslate>Minnen från församlingens händelser</AutoTranslate>}
            </p>
          </div>
        </div>
      </section>

      {/* Albums grid */}
      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center text-gray-500 py-12" data-testid="albums-loading">
              <AutoTranslate>Laddar album...</AutoTranslate>
            </div>
          ) : albums.length === 0 ? (
            <div className="text-center py-16" data-testid="albums-empty">
              <Camera className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-serif font-bold text-gray-700 mb-2">
                <AutoTranslate>Inga album ännu</AutoTranslate>
              </h3>
              <p className="text-gray-500">
                <AutoTranslate>Kom tillbaka snart för att se bilder från våra händelser.</AutoTranslate>
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {albums.map((album) => (
                <Link
                  key={album.id}
                  to={`/forsamling/album/${album.id}`}
                  data-testid={`album-card-${album.id}`}
                  className="block group"
                >
                  <Card className="overflow-hidden border-0 shadow-md hover:shadow-xl transition-shadow duration-300 h-full">
                    <div className="relative aspect-[4/3] bg-gray-100 overflow-hidden">
                      {album.cover_image_url ? (
                        <img
                          src={album.cover_image_url}
                          alt={getLocalized(album.title, language)}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300">
                          <ImageIcon className="w-16 h-16" />
                        </div>
                      )}
                      <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                        <ImageIcon className="w-3 h-3" />
                        {album.photo_count || 0}
                      </div>
                    </div>
                    <CardContent className="p-5">
                      <h3 className="text-lg font-serif font-bold text-gray-900 mb-2 line-clamp-2 hyphens-auto break-words">
                        {getLocalized(album.title, language)}
                      </h3>
                      <div className="flex items-center text-sm text-gray-500 gap-2 mb-2">
                        <Calendar className="w-4 h-4 flex-shrink-0" />
                        <span>{formatEventDate(album.event_date, language)}</span>
                      </div>
                      {getLocalized(album.description, language) && (
                        <p className="text-sm text-gray-600 line-clamp-3" data-testid={`album-description-${album.id}`}>
                          {getLocalized(album.description, language)}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AlbumsListPage;
